/**
 * Tipos de ProblemDetail (RFC 7807) que devuelve facturación.
 *
 * El `type` es la clave estable del error: el título y el detalle son texto para
 * el usuario y se reescriben sin aviso, así que machear por texto se rompe sola.
 */
export const PROBLEM_TYPES = {
  CLIENTE_DUPLICADO: "https://softnik.tech/problems/duplicate-client-document",
};

const PROBLEM_TYPE_PREFIX = "https://softnik.tech/problems/";

/** El `type` del ProblemDetail, o null si la respuesta no es uno. */
export const extractProblemType = (e) => {
  const type = e?.response?.data?.type;
  return typeof type === "string" && type.startsWith(PROBLEM_TYPE_PREFIX) ? type : null;
};

/**
 * El campo que el backend señala como el que hay que completar o cambiar para
 * que la operación entre (propiedad `campo` del ProblemDetail). Sirve para
 * enfocar y marcar ese input en vez de mostrar sólo un cartel.
 */
export const extractApiErrorField = (e) => {
  const campo = e?.response?.data?.campo;
  return typeof campo === "string" && campo.trim() ? campo.trim() : null;
};

export const extractApiErrorMessage = (e, fallback = "Ocurrió un error.") => {
  const data = e?.response?.data;

  const normalize = (value) => (typeof value === "string" ? value.trim() : "");
  const getCodeFromText = (value) => {
    const match = value.match(/code\s*['\"]([^'\"]+)['\"]/i) || value.match(/código\s*['\"]([^'\"]+)['\"]/i);
    return match?.[1] || null;
  };

  const translateGenericText = (text) => {
    if (!text) return "";

    let translated = text;
    translated = translated.replace(/\balready exists\b/gi, "ya existe");
    translated = translated.replace(/\balready in use\b/gi, "ya está en uso");
    translated = translated.replace(/\bnot found\b/gi, "no se encontró");
    translated = translated.replace(/\bdoes not exist\b/gi, "no existe");
    translated = translated.replace(/\binvalid\b/gi, "inválido");
    translated = translated.replace(/\bbad request\b/gi, "solicitud incorrecta");
    translated = translated.replace(/\bunauthorized\b/gi, "no autorizado");
    translated = translated.replace(/\bforbidden\b/gi, "prohibido");
    translated = translated.replace(/\bconflict\b/gi, "conflicto");
    translated = translated.replace(/\brequired\b/gi, "requerido");
    translated = translated.replace(/\bnot allowed\b/gi, "no está permitido");
    translated = translated.replace(/\bcannot\b/gi, "no se puede");
    translated = translated.replace(/\bmust\b/gi, "debe");
    translated = translated.replace(/\bfailed\b/gi, "falló");
    translated = translated.replace(/\btimeout\b/gi, "tiempo de espera agotado");
    translated = translated.replace(/\btry again later\b/gi, "intente nuevamente más tarde");
    translated = translated.replace(/\bplease\b/gi, "por favor");
    translated = translated.replace(/\bservice\b/gi, "servicio");
    translated = translated.replace(/\bclient\b/gi, "cliente");
    translated = translated.replace(/\bissuer\b/gi, "emisor");
    translated = translated.replace(/\brequest\b/gi, "solicitud");
    translated = translated.replace(/\bparameter\b/gi, "parámetro");
    translated = translated.replace(/\bfield\b/gi, "campo");
    translated = translated.replace(/\bconfiguration\b/gi, "configuración");
    translated = translated.replace(/\bmissing\b/gi, "faltante");
    return translated;
  };

  const translateKnownProblem = (title, detail, message) => {
    const combined = [title, detail, message].filter(Boolean).join("\n").trim();
    const lower = combined.toLowerCase();

    if (!combined) return null;

    if ((/service code already in use/i.test(combined) || /duplicate service code/i.test(lower)) && /service/i.test(lower)) {
      const code = getCodeFromText(combined);
      return code ? `Ya existe un servicio con el código "${code}".` : "El código del servicio ya está en uso.";
    }

    if ((/inactive service with this code already exists/i.test(lower) || /inactive service/i.test(lower)) && /service/i.test(lower)) {
      const code = getCodeFromText(combined);
      return code ? `Ya existe un servicio inactivo con el código "${code}".` : "Ya existe un servicio inactivo con ese código.";
    }

    if (/already exists/i.test(lower) && /service/i.test(lower)) {
      const code = getCodeFromText(combined);
      return code ? `Ya existe un servicio con el código "${code}".` : "Ya existe un servicio con ese código.";
    }

    if (/already exists/i.test(lower) && /client/i.test(lower)) {
      return "El cliente ya existe.";
    }

    if (/issuer fiscal configuration missing/i.test(lower)) {
      return "Falta la configuración fiscal del emisor.";
    }

    if (/invalid request/i.test(lower) || /bad request/i.test(lower)) {
      return "La solicitud es inválida.";
    }

    if (/not found/i.test(lower) || /does not exist/i.test(lower)) {
      return "No se encontró el recurso solicitado.";
    }

    if (/already cancelled/i.test(lower)) {
      return "El comprobante ya está cancelado.";
    }

    if (/already invoiced/i.test(lower)) {
      return "El lote ya fue facturado para este período.";
    }

    return null;
  };

  // 0) ProblemDetail del backend: ya viene escrito en español y nombra el campo
  // a corregir. Se devuelve tal cual —traducirlo por palabras sólo lo degrada, y
  // el reemplazo de "client" por "cliente" le comería la palabra a "cliente".
  if (extractProblemType(e)) {
    const delBackend = normalize(data?.detail) || normalize(data?.title);
    if (delBackend) return delBackend;
  }

  // 1) Si viene details como array (como el caso 1528), priorizar
  if (Array.isArray(data?.details) && data.details.length > 0) {
    return translateGenericText(String(data.details[0]));
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];
    return translateGenericText(typeof firstError === "string" ? firstError : firstError?.message || firstError?.detail || "");
  }

  // 2) Mensajes del backend (detail/title) para mostrar la causa real
  const detail = normalize(data?.detail);
  const title = normalize(data?.title);
  const message = normalize(data?.message);
  const translated = translateKnownProblem(title, detail, message);
  if (translated) {
    return translated;
  }

  if (detail) {
    return translateGenericText(detail);
  }

  if (title) {
    return translateGenericText(title);
  }

  // 3) Mensaje estándar
  if (message) {
    return translateGenericText(message);
  }

  const errorText = normalize(data?.error);
  if (errorText) {
    return translateGenericText(errorText);
  }

  // 4) Axios/network
  const axiosMessage = normalize(e?.message);
  if (axiosMessage) {
    return translateGenericText(axiosMessage);
  }

  return fallback;
};
